import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import React from "react";
import { useNavigate } from "react-router-dom";

import { getNavbarStyles } from "../theme/siteTheme";

export interface NavLink {
  href: string;
  label: string;
  dropdown?: { href: string; label: string }[];
}

interface NavbarProps {
  navLinks: NavLink[];
}

const Navbar: React.FC<NavbarProps> = ({ navLinks }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const styles = getNavbarStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [dropdownAnchorEl, setDropdownAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [activeDropdown, setActiveDropdown] = React.useState<string | null>(
    null
  );

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDropdownOpen = (
    event: React.MouseEvent<HTMLElement>,
    linkHref: string
  ) => {
    setDropdownAnchorEl(event.currentTarget);
    setActiveDropdown(linkHref);
  };

  const handleDropdownClose = () => {
    setDropdownAnchorEl(null);
    setActiveDropdown(null);
  };

  return (
    <AppBar position="static" elevation={0} sx={styles.appBar}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={styles.toolbar}>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={styles.brand}
            onClick={() => navigate("/")}
          >
            <Box component="span" sx={styles.brandAccent}>
              Steeldart in BW
            </Box>
          </Typography>
          {isMobile ? (
            <>
              <IconButton
                size="large"
                edge="end"
                aria-label="menu"
                onClick={handleMenu}
                sx={styles.menuIconButton}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{ sx: styles.menuPaper }}
              >
                {navLinks.map((link) => (
                  <div key={link.href}>
                    {link.dropdown ? (
                      <>
                        <MenuItem
                          onClick={(e) => handleDropdownOpen(e, link.href)}
                          sx={styles.menuItem}
                        >
                          {link.label} ▼
                        </MenuItem>
                        <Menu
                          anchorEl={dropdownAnchorEl}
                          open={activeDropdown === link.href}
                          onClose={handleDropdownClose}
                          anchorOrigin={{ vertical: "top", horizontal: "left" }}
                          transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                          }}
                          PaperProps={{ sx: styles.menuPaper }}
                        >
                          {link.dropdown.map((dropdownItem) => (
                            <MenuItem
                              key={dropdownItem.href}
                              onClick={() => {
                                handleClose();
                                handleDropdownClose();
                              }}
                              component="a"
                              href={dropdownItem.href}
                              sx={styles.menuItem}
                            >
                              {dropdownItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    ) : (
                      <MenuItem
                        onClick={handleClose}
                        component="a"
                        href={link.href}
                        sx={styles.menuItem}
                      >
                        {link.label}
                      </MenuItem>
                    )}
                  </div>
                ))}
              </Menu>
            </>
          ) : (
            <Box sx={styles.desktopNav}>
              {navLinks.map((link) => (
                <div key={link.href}>
                  {link.dropdown ? (
                    <>
                      <Button
                        onClick={(e) => handleDropdownOpen(e, link.href)}
                        sx={styles.navButton}
                      >
                        {link.label} ▼
                      </Button>
                      <Menu
                        anchorEl={dropdownAnchorEl}
                        open={activeDropdown === link.href}
                        onClose={handleDropdownClose}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "left",
                        }}
                        PaperProps={{ sx: styles.menuPaper }}
                      >
                        {link.dropdown.map((dropdownItem) => (
                          <MenuItem
                            key={dropdownItem.href}
                            onClick={handleDropdownClose}
                            component="a"
                            href={dropdownItem.href}
                            sx={styles.menuItem}
                          >
                            {dropdownItem.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </>
                  ) : (
                    <Button
                      href={link.href}
                      sx={styles.navButton}
                    >
                      {link.label}
                    </Button>
                  )}
                </div>
              ))}
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
